import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
} from '@mui/material';
import * as Yup from 'yup';
import { capitalCase } from 'capital-case';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { FormProvider, RHFSelect } from 'src/components/hook-form';
import { WrappedCarePlan } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import { CaseIntent, CaseStatus } from 'src/crs/case/dto/case.dto';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import CancelButton from 'src/careflow/common/CancelButton';
import { WorkflowService } from 'src/careflow/ph-core/workflow/api/WorkflowService';
import { useSnackbar } from 'notistack';

type FormValue = {
  patientId: string;
  intent: string;
  status: string;
  workflowScope?: string;
};

type Props = {
  patient: WrappedPatient | null;
  carePlan: WrappedCarePlan | null;
  open: boolean;
  onClose: VoidFunction;
  handleCase: (data: any) => Promise<any>;
  existData: boolean;
  externalQuery?: string;
};

export function CaseForm({
  patient,
  carePlan,
  open,
  onClose,
  handleCase,
  existData,
  externalQuery,
}: Props) {
  const { i18n } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const { configurations } = useTenantConfigData();
  const [workflows, setWorkflows] = useState<any>(null);

  const EventSchema = Yup.object().shape({
    patientId: Yup.string().required(`${i18n('patients.title', 'crs')}Id is required`),
    workflowScope: Yup.string().required('Scope is required'),
    intent: Yup.string().required('Intent is required'),
    status: Yup.string().required('Status is required'),
  });

  const defaultValues = useMemo(
    () =>
      ({
        workflowScope: '',
        patientId: patient?.id || '',
        intent: carePlan?.intent || CaseIntent.PROPOSAL,
        status: carePlan?.status || CaseStatus.ACTIVE,
      } as FormValue),
    [carePlan?.intent, carePlan?.status, patient?.id]
  );

  const methods = useForm<FormValue>({ resolver: yupResolver(EventSchema) });
  const { reset, setValue, handleSubmit } = methods;

  const getWorkflowsByType = useCallback(async () => {
    const query = externalQuery ?? 'type=case';
    const workflowsByType = await WorkflowService.getWorkflowQuery(query);
    const normalizedWorkflows = Array.isArray(workflowsByType)
      ? workflowsByType
      : [workflowsByType];
    const onlyActive = normalizedWorkflows.filter((item: any) => item?.status === 'Active');

    setWorkflows(onlyActive);
    if (onlyActive?.length === 1) setValue('workflowScope', onlyActive?.[0]?.code);
  }, [externalQuery, setValue]);

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      getWorkflowsByType();
    } else {
      setValue('workflowScope', '');
    }
  }, [defaultValues, getWorkflowsByType, open, reset, setValue]);

  const mapCarePlan = (dataForm: FormValue) => ({
    resourceType: 'CarePlan',
    ...dataForm,
    created: new Date().toISOString(),
    scope: [
      {
        coding: [
          {
            system: configurations?.fhir?.caseCategorySystem,
            code: configurations?.fhir?.mccCaseScopeCode,
            display: configurations?.fhir?.mccCaseScopeDisplay,
          },
        ],
        text: configurations?.fhir?.mccCaseScopeDisplay,
      },
    ],
    ...(carePlan !== null && { id: carePlan?.id }),
  });

  const validateWorkflowConditions = async (scope?: string): Promise<boolean> => {
    const conditions = workflows?.find((item) => item?.code === scope)?.conditions ?? null;

    if (!conditions) return true;

    return WorkflowService.validateWorkflowCondition({
      item: {
        indexedSubjectFhirRefUri: `${patient?.resourceType}/${patient?.id}`,
      },
      conditions,
    });
  };

  const onSubmit = async (dataForm: FormValue) => {
    const isValid = existData ? await validateWorkflowConditions(dataForm?.workflowScope) : true;

    if (!isValid) {
      enqueueSnackbar(i18n('patients.details.cases.validateWorkflowMessage', 'crs'), {
        variant: 'error',
      });
      return;
    }

    const resultMap = mapCarePlan(dataForm);
    handleCase(resultMap);
    handleClose();
  };

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  return (
    <Dialog open={open} fullWidth={true} maxWidth="md">
      <DialogTitle>
        {carePlan
          ? i18n('patients.details.cases.titleEditCases', 'crs')
          : i18n('patients.details.cases.titleAddCases', 'crs')}{' '}
      </DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Card sx={{ m: 2 }}>
          <Grid container>
            <Grid item xs={12}>
              <Stack spacing={2} sx={{ p: 2 }}>
                <RHFSelect name="workflowScope" label={'Scope'}>
                  <MenuItem disabled />
                  {workflows?.map((option) => (
                    <MenuItem key={option} value={option?.code}>
                      {option?.name}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={2} sx={{ p: 2 }}>
                <RHFSelect
                  name="status"
                  label={i18n('patients.details.cases.statusAddCases', 'crs')}
                >
                  <MenuItem disabled />
                  {Object.entries(CaseStatus).map((option) => (
                    <MenuItem key={option[0]} value={option[1]}>
                      {capitalCase(option[1])}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={2} sx={{ p: 2 }}>
                <RHFSelect name="intent" label={i18n('patients.details.cases.intent', 'crs')}>
                  <MenuItem disabled />
                  {Object.entries(CaseIntent).map((option) => (
                    <MenuItem key={option[0]} value={option[1]}>
                      {capitalCase(option[1])}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </Stack>
            </Grid>
          </Grid>
          <Stack spacing={2} alignItems="center">
            <DialogActions>
              <Box sx={{ flexGrow: 1 }} />

              <CancelButton onClick={handleClose}>{i18n('cancel')}</CancelButton>

              <Button variant="contained" type="submit">
                {i18n('saveChanges')}
              </Button>
            </DialogActions>
          </Stack>
        </Card>
      </FormProvider>
    </Dialog>
  );
}
